import { useEffect, useState, useRef, use } from "react";
import axios from "axios";
import {
  Container, Row, Col, Card, Form, Alert, Spinner, Table, Modal, Button, Badge
} from "react-bootstrap";
import LineMonthSeries from "./LineMonthSeries";
import LineYearComparison from "./LineYearComparison";

export default function ReportesContables() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [tipos, setTipos] = useState({});
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [morosos, setMorosos] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMorosos, setShowMorosos] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [yearComparisonData, setYearComparisonData] = useState([]);

  const [monthlyStats, setMonthlyStats] = useState({
    total: 0,
    previous: 0,
    change: 0,
    morosos: 0
  });

  const [yearComparison, setYearComparison] = useState({
    current: 0,
    previous: 0,
    change: 0
  });

  const nameCache = useRef({ tipos: {} });

  const mes = new Date().toLocaleString("es-CL", { month: "long" }).toUpperCase();

  const filteredMorosos = morosos.filter((m) => {
    const texto = search.toLowerCase();
    return (
      m.nombre?.toLowerCase().includes(texto) ||
      m.apellido?.toLowerCase().includes(texto) ||
      m.rut?.toLowerCase().includes(texto)
    );
  });

  const totalPages = Math.ceil(filteredMorosos.length / rowsPerPage);

  const paginatedMorosos = filteredMorosos.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalRecaudado = summary?.total_monto?.[0]?.total_recaudado || 0;
  const totalPagos = summary?.total_monto?.[0]?.pagos_count || 0;

  const totalSocios = morosos.length + totalPagos;
  const tasaMorosidad = totalSocios > 0
    ? ((morosos.length / totalSocios) * 100).toFixed(1)
    : 0;

  const cumplimiento = totalSocios > 0
    ? ((totalPagos / totalSocios) * 100).toFixed(1)
    : 0;

  const morosidadColor = tasaMorosidad > 30 ? "danger" : tasaMorosidad > 15 ? "warning" : "success";
  const cumplimientoColor = cumplimiento < 70 ? "danger" : cumplimiento < 85 ? "warning" : "success";

  const formatMoney = (n) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(n || 0);


  const getUltimoMesPagado = (pagos = []) => {
    if (!pagos.length) return "N/A";
    const sorted = [...pagos].sort((a, b) => a.año - b.año || a.mes - b.mes);
    const last = sorted[sorted.length - 1];
    return `${last.mes}/${last.año}`;
  };

  const mesesOrdenados = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const monthlyNormalized = mesesOrdenados.map((mes) => {
    const found = monthly.find((m) => m.mes === mes.toLowerCase()); return {
      mes,
      total: found ? Number(found.total) : 0
    };
  });

  // useEffect(async () => {
  //   const tiposRes = await axios.get("https://socios.kolpingchile.cl/backend/api/tipos");

  //   const tiposMap = {};
  //   tiposRes.data.forEach(t => {
  //     tiposMap[t.id] = t.nombre;
  //   });

  //   setTipos(tiposMap);
  // }, []);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const comparisonYear = currentMonth === 1 ? year - 1 : year;

        const [summaryRes, monthlyRes, morososRes, comparisonRes, compareYearRes, compareRes, tiposRes] = await Promise.all([
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/${year}`),
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/monthly/${year}`),
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/morosos/${year}/${currentMonth}`),
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/compare-months/${comparisonYear}/${previousMonth}/${currentMonth}`),
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/compare-years/${year}`),
          axios.get(`https://socios.kolpingchile.cl/backend/api/reportes/compare-years-monthly/${year}`),
          axios.get("https://socios.kolpingchile.cl/backend/api/tipos")
        ]);

        setSummary(summaryRes.data);
        setMonthly(monthlyRes.data.months || []);
        interpret(comparisonRes.data.rows);
        setYearComparison(compareYearRes.data);
        setYearComparisonData(compareRes.data || []);
        const tiposMap = {};
        tiposRes.data.forEach(t => {
          tiposMap[t.id] = t.nombre;
        });
        setTipos(tiposMap);

        const rows = comparisonRes.data.rows;

        const current = parseFloat(rows.find(r => r.periodo === "current")?.total || 0);
        const previous = parseFloat(rows.find(r => r.periodo === "previous")?.total || 0);

        const change = previous > 0 ? ((current - previous) / previous) * 100 : 0;

        setMonthlyStats({
          total: current,
          previous,
          change,
          morosos: morososRes.data.morosos.length
        });
        const morososWithTipo = await Promise.all(
          (morososRes.data.morosos || []).map(async (m) => ({
            ...m,
          }))
        );



        setMorosos(morososWithTipo);
      } catch {
        setAlert("⚠️ Error al cargar los reportes");
      } finally {
        setLoading(false);
      }
    }

    fetchAll();
  }, [year]);

  function interpret(data) {
    if (!data || data.length < 2) return;
    const curr = parseFloat(data.find((d) => d.periodo === "current")?.total || 0);
    const prev = parseFloat(data.find((d) => d.periodo === "previous")?.total || 0);

    if (prev > 0) {
      const change = ((curr - prev) / prev) * 100;
      if (change < -5)
        setAlert(`⚠️ Caída de ${change.toFixed(1)}% en recaudación`);
      else if (change > 5)
        setAlert(`✅ Aumento de ${change.toFixed(1)}% en recaudación`);
      else
        setAlert(`ℹ️ Recaudación estable`);
    }
  }

  return (
    <Container fluid className="py-4">
      {/* Year Filter */}
      <Row className="mb-4 align-items-end">
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label className="fw-bold">Año</Form.Label>
            <Form.Select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Alert */}
      {/* {alert && <Alert variant="info" className="shadow-sm mb-4">{alert}</Alert>} */}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3 text-muted">Cargando datos...</p>
        </div>
      ) : (
        <>

          <h2 className="fw-bold mb-4">Resumen {year}</h2>

          {/* Summary Cards */}
          <Row className="g-3 mb-4">
            {/* Total Recaudado */}
            <Col xs={12} sm={6} lg={3}>
              <Card className="shadow-sm border-0 rounded-4 h-100">
                <Card.Body>
                  <small className="text-muted d-block mb-2">Total Recaudado</small>
                  <Badge bg="success" style={{ fontSize: "1.5rem" }} className="fw-bold mb-2">{formatMoney(totalRecaudado)}</Badge>
                  {/* <span className="text-success small">↑ 12% vs mes anterior</span> */}
                  <div className={`small ${yearComparison.change > 0 ? 'text-success' :
                    yearComparison.change < 0 ? 'text-danger' :
                      'text-warning'
                    }`}>
                    {yearComparison.change > 0 ? '▲' : '▼'} {yearComparison.change.toFixed(1)}%
                    <span className="text-muted"> vs año anterior</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Pagos */}
            <Col xs={12} sm={6} lg={3}>
              <Card className="shadow-sm border-0 rounded-4 h-100">
                <Card.Body>
                  <small className="text-muted d-block mb-2">Pagos Registrados</small>
                  <Badge bg="primary" style={{ fontSize: "1.5rem" }} className="fw-bold">
                    {totalPagos}
                  </Badge>
                </Card.Body>
              </Card>
            </Col>

            {/* Morosidad */}
            <Col xs={12} sm={6} lg={3}>
              <Card className="shadow-sm border-0 rounded-4 h-100">
                <Card.Body>
                  <small className="text-muted d-block mb-2">Tasa de Morosidad</small>
                  <Badge bg={morosidadColor} style={{ fontSize: "1.5rem" }} className="fw-bold">
                    {tasaMorosidad}%
                  </Badge>
                </Card.Body>
              </Card>
            </Col>

            {/* Cumplimiento */}
            <Col xs={12} sm={6} lg={3}>
              <Card className="shadow-sm border-0 rounded-4 h-100">
                <Card.Body>
                  <small className="text-muted d-block mb-2">Cumplimiento</small>
                  <Badge bg={cumplimientoColor} style={{ fontSize: "1.5rem" }} className="fw-bold">
                    {cumplimiento}%
                  </Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>


          {/* Details Row */}
          <Row className="g-3 mb-4">
            {/* Cuotas Especiales */}
            <Col xs={12} lg={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">Resumen Mensual</Card.Title>
                  <p className="text-muted mb-4">{mes}</p>
                  <hr />
                  <Row className="g-4 mb-4">

                    {/* 💰 Recaudación mes */}
                    <Col md={12}>
                      <Card className="shadow-sm border-0 rounded-4">
                        <Card.Body>
                          <small className="text-muted">Recaudación Mes Actual</small>
                          <h4 className="fw-bold">{formatMoney(monthlyStats.total)}</h4>
                          <span className={`small ${monthlyStats.change >= 0 ? 'text-success' : 'text-danger'}`}>
                            {monthlyStats.change >= 0 ? '▲' : '▼'} {monthlyStats.change.toFixed(1)}%
                          </span>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* 📉 Mes anterior */}
                    <Col md={6}>
                      <Card className="shadow-sm border-0 rounded-4">
                        <Card.Body>
                          <small className="text-muted">Recaudación Mes anterior</small>
                          <h4 className="fw-bold">{formatMoney(monthlyStats.previous)}</h4>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* ⚠️ Morosos */}
                    <Col md={6}>
                      <Card className="shadow-sm border-0 rounded-4">
                        <Card.Body>
                          <small className="text-muted">Morosos mes</small>
                          <h4 className="fw-bold text-danger">{monthlyStats.morosos}</h4>
                        </Card.Body>
                      </Card>
                    </Col>



                  </Row>

                </Card.Body>
              </Card>
            </Col>

            {/* Monthly Chart */}
            <Col xs={12} lg={8}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">Ingresos Mensuales</Card.Title>
                  {monthly.length > 0 ? (
                    <LineMonthSeries data={monthlyNormalized} year={year} />
                  ) : (
                    <p className="text-muted text-center py-4">Sin datos disponibles</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <Card.Title className="fw-bold">Comparación Año vs Año</Card.Title>

                  {yearComparisonData.length > 0 ? (
                    <LineYearComparison data={yearComparisonData} year={year} />
                  ) : (
                    <p className="text-muted">Sin datos</p>
                  )}

                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Morosos Section */}
          <Row className="g-3 mt-3">
            <Col xs={12}>
              <Card className="shadow-sm border-0">
                <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <Card.Title className="fw-bold mb-0">Morosos</Card.Title>
                    <p className="text-muted mb-0">Total: {morosos.length}</p>
                  </div>
                  <Button
                    onClick={() => setShowMorosos(true)}
                    variant="primary"
                    size="sm"
                  >
                    Ver Detalle
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Morosos Modal */}
          <Modal size="lg" show={showMorosos} onHide={() => setShowMorosos(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Listado de Morosos</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {/* Controls */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                {/* Search */}
                <div className="d-flex gap-2 flex-wrap">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Buscar por nombre o RUT..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ minWidth: "220px" }}
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setCurrentPage(1)}
                  >
                    Buscar
                  </Button>
                </div>

                {/* Rows Per Page */}
                <div className="d-flex align-items-center gap-2">
                  <span>Mostrar:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: "90px" }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive">
                <Table striped hover className="mb-4">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>RUT</th>
                      <th>Tipo</th>
                      <th>Último Pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMorosos.length > 0 ? (
                      paginatedMorosos.map((m) => (
                        <tr key={m.n_socio}>
                          <td>{m.nombre} {m.apellido}</td>
                          <td>{m.rut}</td>
                          <td>{tipos[m.tipo_socio] || "N/A"}</td>
                          <td>{m.ultimo_pago || "N/A"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No se encontraron registros
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="text-muted small">
                  Página {currentPage} de {totalPages || 1}
                </div>

                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    ← Anterior
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Siguiente →
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        </>
      )}
    </Container>
  );
}
